import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox } from '@mui/material';
import { ValidationInput, CreateChancel, MaskInput, AddressInput, SingleSearch, MinLoader } from 'components';
import { clientActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import {
    Colors,
    EmailValidator,
    ErrorText,
    FindErrorItem,
    FindLoad,
    FindSuccess,
    getPhoneErrorText,
    hooksForErrors,
    Images,
    isNotEmpty,
    useModal,
} from 'utils';
import axios from 'axios';

export const AddContact = ({ info }) => {
    const clientContact = useSelector((state) => state.client.clientContacts);
    const client = useSelector((state) => state.client.clientItemInfo);
    const dispatch = useDispatch();
    const { close } = useModal();
    const isCreate = !info;
    const LINK_ACTION_TYPE = 'LINK_CONTACT_TO_CLIENT';
    const ACTION_TYPE = info ? 'EDIT_CLIENT_CONTACT' : 'CREATE_CLIENT_CONTACT';
    const success = FindSuccess(ACTION_TYPE);
    const linkSuccess = FindSuccess(LINK_ACTION_TYPE);
    const loader = FindLoad(ACTION_TYPE);
    const linkLoader = FindLoad(LINK_ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const [error, setError] = useState('');
    const [inputs, setInputs] = useState(info ? { ...info } : {});
    const [enteredAddress, setEnteredAddress] = useState(info ? info.address : '');
    const phoneErrorMsg = getPhoneErrorText(inputs?.phoneNumber);
    const phoneErrorText = hooksForErrors.getPhoneError(error, backError, phoneErrorMsg);
    const secondaryPhoneErrorText = hooksForErrors.getPhoneError(error, backError, phoneErrorMsg);
    const emailErrorMsg = !EmailValidator.test(inputs.email) ? ErrorText.emailValid : '';
    const emailErrorText = hooksForErrors.getEmailError(error, backError, emailErrorMsg);
    const [legalGuardian, setLegalGuardian] = useState(info?.legalGuardian ? info?.legalGuardian : false);
    const defaultGuardian = clientContact?.find((item) => item?.legalGuardian);
    const phoneBackError = backError?.error?.[0];
    // Search existing contacts state (create mode only)
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedExistingId, setSelectedExistingId] = useState(null);
    const [selectedHasPortalAccount, setSelectedHasPortalAccount] = useState(false);
    const [grantPortalAccess, setGrantPortalAccess] = useState(false);
    const searchTimerRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(
        () => () => {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            dispatch(httpRequestsOnErrorsActions.removeError(LINK_ACTION_TYPE));
        },
        []
    );

    useEffect(() => {
        if (!!success.length) {
            close();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    useEffect(() => {
        if (!!linkSuccess.length) {
            close();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(LINK_ACTION_TYPE));
        }
    }, [linkSuccess]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const searchContacts = useCallback(async (value) => {
        if (!value || value.trim().length < 2) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }
        setSearchLoading(true);
        try {
            const res = await axios.get('/contact/search', {
                auth: true,
                params: { searchValue: value.trim(), excludeClientId: client?.id },
            });
            setSearchResults(res?.data || []);
            setShowDropdown(true);
        } catch (e) {
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    }, [client?.id]);

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (selectedExistingId) {
            handleClearSelection();
        }
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
        searchTimerRef.current = setTimeout(() => {
            searchContacts(value);
        }, 300);
    };

    const handleSelectContact = (contact) => {
        setSelectedExistingId(contact.id);
        setSelectedHasPortalAccount(!!contact.hasPortalAccount);
        setGrantPortalAccess(false);
        setSearchTerm(`${contact.firstName} ${contact.lastName}`);
        setShowDropdown(false);
        setInputs({
            firstName: contact.firstName || '',
            lastName: contact.lastName || '',
            phoneNumber: contact?.phoneNumber || '',
            phoneSecondary: contact?.phoneSecondary || '',
            email: contact?.email || '',
            relationship: contact?.relationship || '',
        });
        setEnteredAddress(contact?.address || '');
        setLegalGuardian(contact?.legalGuardian || false);
        setError('');
    };

    const handleClearSelection = () => {
        setSelectedExistingId(null);
        setSelectedHasPortalAccount(false);
        setGrantPortalAccess(false);
        setSearchTerm('');
        setInputs({});
        setEnteredAddress('');
        setLegalGuardian(false);
        setSearchResults([]);
        setShowDropdown(false);
    };

    const handleChange = (e) => {
        setInputs((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value,
        }));
        if (error === e.target.name || error === phoneErrorMsg || !!backError) {
            setError('');
        }
        if (!!backError) {
            dispatch(httpRequestsOnErrorsActions.removeError(backError.type));
        }
    };

    const handleCreate = () => {
        if (selectedExistingId) {
            dispatch(clientActions.linkContactToClient(selectedExistingId, client?.id, grantPortalAccess || undefined));
            return;
        }

        // const phoneIsValid = inputs?.phoneNumber ? inputs.phoneNumber.trim().length >= 10 && !/[a-zA-Z]/g.test(inputs.phoneNumber) : true;
        // const secPhoneIsValid = inputs?.phoneSecondary ? inputs.phoneSecondary.trim().length >= 10 && !/[a-zA-Z]/g.test(inputs.phoneSecondary) : true;
        const emailIsValid = inputs.email ? EmailValidator.test(inputs.email) : true;
        const contactDataIsValid = isNotEmpty(inputs.firstName) && isNotEmpty(inputs.lastName) && emailIsValid;
        // const contactDataIsValid = isNotEmpty(inputs.firstName) && isNotEmpty(inputs.lastName) && phoneIsValid && emailIsValid && isNotEmpty(inputs.relationship);
        // const addressIsValid = enteredAddress?.street && enteredAddress?.city && enteredAddress?.country;

        // if (contactDataIsValid && addressIsValid) {
        if (contactDataIsValid) {
            const data = {
                firstName: inputs.firstName,
                lastName: inputs.lastName,
                relationship: inputs?.relationship || null,
                email: inputs?.email ? inputs?.email : null,
                address: enteredAddress || null,
                legalGuardian: legalGuardian,
            };
            inputs?.phoneNumber ? (data.phoneNumber = parseInt(inputs?.phoneNumber.replace(/[^0-9]/g, '')) + '') : delete data.phoneNumber;
            inputs?.phoneSecondary ? (data.phoneSecondary = parseInt(inputs?.phoneSecondary.replace(/[^0-9]/g, '')) + '') : delete data.phoneSecondary;

            if (!!info) {
                dispatch(clientActions.editClientContact(data, info?.id, client?.id));
            } else {
                dispatch(clientActions.createClientContact(data, client?.id));
            }
        } else {
            const contactDataErrorText = !isNotEmpty(inputs.firstName)
                ? 'firstName'
                : !isNotEmpty(inputs.lastName)
                  ? 'lastName'
                  : !phoneIsValid
                    ? phoneErrorMsg
                    : !emailIsValid
                      ? emailErrorMsg
                      : !isNotEmpty(inputs.relationship)
                        ? 'relationship'
                        : !addressIsValid
                          ? 'address'
                          : '';
            setError(contactDataErrorText);
        }
    };

    const isLoading = !!loader.length || !!linkLoader.length;

    return (
        <div className="client-contact-modal">
            <p className="modal-header-title">{info ? 'Edit Contact' : 'Add Contact'}</p>

            {isCreate && (
                <div className="contact-search-wrapper" ref={dropdownRef}>
                    <div className="contact-search-input-box">
                        <SingleSearch
                            loading={searchLoading}
                            className={'contact-search-input'}
                            placeholder={'Search existing contacts by name or email...'}
                            setSearchTerm={handleSearchChange}
                            searchTerm={searchTerm}
                        />

                        {/*{selectedExistingId && (*/}
                        {/*    <span className="contact-search-clear" onClick={handleClearSelection}>*/}
                        {/*        ✕*/}
                        {/*    </span>*/}
                        {/*)}*/}
                    </div>
                    {selectedExistingId && (
                        <p className="contact-search-linked-text">Existing contact selected — will be linked to this client</p>
                    )}
                    {selectedExistingId && selectedHasPortalAccount && (
                        <div className="contact-search-portal-access">
                            <Checkbox
                                checked={grantPortalAccess}
                                onClick={() => setGrantPortalAccess(!grantPortalAccess)}
                                style={{ padding: 0, color: '#347AF0', marginRight: 6 }}
                            />
                            <p>
                                Grant portal access to {client?.firstName} {client?.lastName}
                            </p>
                        </div>
                    )}
                    {showDropdown && (
                        <>
                            <div style={{ position: 'relative', marginTop: 20 }}>
                                <div className="suggestion-list-wrapper">
                                    {searchLoading ? (
                                        <div style={{ marginBottom: 8 }}>Loading...</div>
                                    ) : (
                                        <div className="flex-end">
                                            <button className="reset-btn-by-default" type={'button'} onClick={() => setShowDropdown(false)}>
                                                <img src={Images.closeGray} alt="close" />
                                            </button>
                                        </div>
                                    )}

                                    {!!searchResults?.length
                                        ? searchResults.map((contact) => (
                                              <div key={contact.id} onClick={() => handleSelectContact(contact)}>
                                                  <p>
                                                      <span style={{ fontWeight: 500 }}>
                                                          {contact.firstName} {contact.lastName}
                                                      </span>
                                                      {contact.email && (
                                                          <span style={{ color: '#667085', marginLeft: 8 }}>— {contact.email}</span>
                                                      )}
                                                  </p>
                                              </div>
                                          ))
                                        : !searchLoading && searchTerm.trim().length >= 2 && <p>No matching contacts found</p>}
                                </div>
                            </div>
                            <div className="beck-drop" onClick={() => setShowDropdown(false)} />
                        </>
                    )}
                </div>
            )}

            <div className="client-contact-modal-body">
                <div className="full-width">
                    <ValidationInput
                        variant={'outlined'}
                        onChange={handleChange}
                        value={inputs.firstName}
                        type={'text'}
                        label={'First Name*'}
                        name="firstName"
                        typeError={error === 'firstName' ? ErrorText.field : ''}
                        max={15}
                        disabled={!!selectedExistingId}
                    />
                    <ValidationInput
                        variant={'outlined'}
                        onChange={handleChange}
                        value={inputs.lastName}
                        type={'text'}
                        label={'Last Name*'}
                        name="lastName"
                        typeError={error === 'lastName' ? ErrorText.field : ''}
                        max={15}
                        disabled={!!selectedExistingId}
                    />
                    <MaskInput
                        name="phoneNumber"
                        label="Phone Number"
                        value={inputs.phoneNumber}
                        handleChange={handleChange}
                        error={phoneBackError === 'phoneNumber must be a valid phone number' && phoneErrorText}
                        disabled={!!selectedExistingId}
                    />
                    <MaskInput
                        name="phoneSecondary"
                        label="Secondary Phone Number"
                        value={inputs.phoneSecondary}
                        handleChange={handleChange}
                        error={phoneBackError === 'phoneSecondary must be a valid phone number' && secondaryPhoneErrorText}
                        disabled={!!selectedExistingId}
                    />
                    <ValidationInput
                        validator={EmailValidator}
                        variant={'outlined'}
                        name={'email'}
                        type={'email'}
                        label={'Email'}
                        typeError={emailErrorText}
                        value={inputs?.email}
                        onChange={handleChange}
                        disabled={!!selectedExistingId}
                    />
                    <ValidationInput
                        variant={'outlined'}
                        onChange={handleChange}
                        value={inputs.relationship}
                        type={'text'}
                        label={'Relationship'}
                        name="relationship"
                        typeError={error === 'relationship' ? ErrorText.field : ''}
                        disabled={!!selectedExistingId}
                    />
                </div>
                <div className="full-width">
                    <AddressInput
                        placeholder={'Physical Address'}
                        selectedAddress={selectedExistingId ? enteredAddress || undefined : info?.address}
                        setCurrentAddress={setEnteredAddress}
                        // errMessage={error === 'address'}
                        disabled={!!selectedExistingId}
                        notRequired={true}
                    />
                </div>
            </div>
            <div className="legal-guardian-box">
                <Checkbox
                    disabled={selectedExistingId ? true : info?.legalGuardian ? false : !!defaultGuardian}
                    onClick={(e) => setLegalGuardian(!legalGuardian)}
                    name={'checkBox'}
                    style={{
                        padding: 0,
                        color: '#347AF0',
                        opacity: selectedExistingId ? 0.5 : info?.legalGuardian ? 1 : !!defaultGuardian ? 0.5 : 1,
                    }}
                    checked={legalGuardian}
                />
                <p style={{ opacity: selectedExistingId ? 0.5 : !!defaultGuardian ? 0.5 : 1 }}>
                    {/*<span style={{ fontWeight: 600 }}>Legal Guardian </span>*/}
                    <span style={{ fontWeight: 600 }}>Default Signer </span>
                    This person will be the default signor for appt.
                </p>
            </div>

            <div className="contact-create-cancel">
                <CreateChancel
                    loader={isLoading}
                    create={info ? 'Save' : selectedExistingId ? 'Link' : 'Add'}
                    chancel={'Cancel'}
                    onCreate={handleCreate}
                    onClose={close}
                    buttonWidth="428px"
                />
            </div>
        </div>
    );
};
